<template>
  <div class="registro-inline">
    <h6 class="ri-titulo">{{ $t('pronostico.registroInlineTitulo') }}</h6>

    <div class="campo">
      <label class="campo-label">{{ $t('pronostico.registroInlineNombre') }}</label>
      <input
        v-model="form.nombre"
        type="text"
        class="campo-input"
        :placeholder="$t('pronostico.namePlaceholder')"
        :disabled="cargando"
        autocomplete="name"
      />
    </div>

    <div class="campo">
      <label class="campo-label">{{ $t('pronostico.registroInlineEmail') }}</label>
      <input
        v-model="form.email"
        type="email"
        class="campo-input"
        placeholder="tu@email.com"
        :disabled="cargando"
        autocomplete="email"
      />
    </div>

    <div class="campo">
      <label class="campo-label">{{ $t('pronostico.registroInlinePais') }} <span class="campo-required">*</span></label>
      <select
        v-model="form.pais"
        class="campo-input"
        :disabled="cargando"
        required
      >
        <option value="">{{ $t('pronostico.registroInlinePaisPlaceholder') }}</option>
        <option v-for="p in paisesOrdenados" :key="p" :value="p">{{ getCountryLabel(p) }}</option>
      </select>
    </div>

    <div class="campo">
      <label class="campo-label">{{ $t('pronostico.registroInlinePassword') }}</label>
      <input
        v-model="form.password"
        type="password"
        class="campo-input"
        :placeholder="$t('pronostico.registroInlinePasswordPlaceholder')"
        :disabled="cargando"
        autocomplete="new-password"
      />
    </div>

    <div class="campo">
      <label class="campo-label">{{ $t('pronostico.registroInlinePasswordConfirm') }}</label>
      <input
        v-model="form.password_confirmation"
        type="password"
        class="campo-input"
        :placeholder="$t('pronostico.registroInlineRepeatPlaceholder')"
        :disabled="cargando"
        autocomplete="new-password"
        @keyup.enter="registrar"
      />
    </div>

    <label class="legal-check">
      <input type="checkbox" v-model="aceptaLegal" :disabled="cargando" />
      <span v-html="textoLegal"></span>
    </label>

    <p v-if="error" class="ri-error">{{ error }}</p>

    <button
      class="ri-btn-crear"
      :disabled="cargando"
      @click.prevent="registrar"
    >
      <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
      {{ cargando ? $t('pronostico.registroInlineCargando') : $t('pronostico.registroInlineBtn') }}
    </button>

    <p class="ri-login-link">
      {{ $t('pronostico.registroInlineYaTengo') }}
      <span class="ri-link" @click="$store.commit('abrirLoginModal')">{{ $t('pronostico.registroInlineIniciar') }}</span>
    </p>
  </div>
</template>

<script>
export default {
  name: "PronosticoRegistroInline",
  props: {
    bloquearUI: { type: Boolean, default: false },
    paises:     { type: Array, default: () => [] },
  },
  emits: ["registrado"],
  data() {
    return {
      cargando: false,
      error: "",
      aceptaLegal: false,
      form: {
        nombre: "",
        email: "",
        pais: "",
        password: "",
        password_confirmation: "",
      },
    };
  },
  computed: {
    paisesOrdenados() {
      return [...this.paises].sort((a, b) =>
        this.getCountryLabel(a).localeCompare(this.getCountryLabel(b))
      );
    },
    textoLegal() {
      const aviso = `<a href="/aviso-legal" target="_blank" rel="noopener" class="legal-link">${this.$t('pronostico.registroInlineAvisoLegal')}</a>`;
      const privacidad = `<a href="/politica-privacidad" target="_blank" rel="noopener" class="legal-link">${this.$t('pronostico.registroInlinePrivacidad')}</a>`;
      return this.$t('pronostico.registroInlineLegal', { aviso, privacidad });
    },
  },
  methods: {
    getCountryLabel(code) {
      try {
        const value = this.$t(`countries.${code}`);
        if (value && value !== `countries.${code}`) return value;
      } catch (_) { /* noop */ }
      try {
        const names = new Intl.DisplayNames([this.$i18n.locale, 'es'], { type: 'region' });
        return names.of(code) || code;
      } catch (_) { return code; }
    },
    async registrar() {
      this.error = "";

      if (!this.form.nombre.trim())
        return (this.error = this.$t('pronostico.registroInlineErrorNombre'));
      if (!this.form.email.trim())
        return (this.error = this.$t('pronostico.registroInlineErrorEmail'));
      if (!this.form.pais)
        return (this.error = this.$t('pronostico.registroInlineErrorPais'));
      if (this.form.password.length < 8)
        return (this.error = this.$t('pronostico.registroInlineErrorPassword'));
      if (this.form.password !== this.form.password_confirmation)
        return (this.error = this.$t('pronostico.registroInlineErrorMatch'));
      if (!this.aceptaLegal)
        return (this.error = this.$t('pronostico.registroInlineErrorLegal'));

      this.cargando = true;
      try {
        const getCookie = (name) => {
          const m = document.cookie.match('(?:^|;)\\s*' + name + '=([^;]*)');
          return m ? decodeURIComponent(m[1]) : null;
        };
        const resp = await this.$store.dispatch("auth/registroRapido", {
          nombre:                this.form.nombre,
          email:                 this.form.email,
          pais:                  this.form.pais,
          password:              this.form.password,
          password_confirmation: this.form.password_confirmation,
          fbc:                   getCookie('_fbc'),
          fbp:                   getCookie('_fbp'),
        });

        if (resp?.res === "OK") {
          this.$emit("registrado", { email: this.form.email, pais: this.form.pais });
        } else {
          this.error = resp?.mensaje || "Ocurrió un error. Intenta de nuevo.";
        }
      } catch (err) {
        const data = err?.response?.data;
        if (data?.errors) {
          if (data.errors.email) {
            this.error = this.$t('pronostico.registroInlineErrorEmailTaken');
          } else {
            const firstKey = Object.keys(data.errors)[0];
            this.error = data.errors[firstKey]?.[0] || this.$t('pronostico.registroInlineErrorNombre');
          }
        } else if (data?.message) {
          this.error = data.message;
        } else {
          this.error = "Ocurrió un error. Intenta de nuevo.";
        }
      } finally {
        this.cargando = false;
      }
    },
  },
};
</script>

<style scoped>
.registro-inline {
  max-width: 400px;
  margin: 0 auto;
  padding: 1.75rem 1.5rem;
  background: var(--q-card);
  border: 1px solid var(--q-border);
  border-radius: 16px;
  text-align: left;
}

.ri-titulo {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--q-text);
  margin-bottom: 1.25rem;
  text-align: center;
}

.campo {
  margin-bottom: 0.9rem;
}

.campo-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--q-text);
  margin-bottom: 4px;
}

.campo-input {
  width: 100%;
  height: 40px;
  background: var(--q-card-alt);
  border: 1px solid var(--q-border);
  border-radius: 8px;
  padding: 0 12px;
  font-size: 0.88rem;
  color: var(--q-text);
  transition: border-color 0.15s;
}
.campo-input:focus {
  outline: none;
  border-color: var(--q-gold);
}
.campo-input:disabled { opacity: 0.6; }

.legal-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.8rem;
  color: var(--q-muted);
  margin: 1rem 0;
  cursor: pointer;
  line-height: 1.45;
}
.legal-check input[type="checkbox"] {
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: var(--q-gold);
  width: 15px;
  height: 15px;
}

.ri-error {
  font-size: 0.82rem;
  color: var(--q-red, #ef4444);
  margin-bottom: 0.75rem;
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.08);
  border-radius: 6px;
}

.ri-btn-crear {
  width: 100%;
  padding: 11px;
  background: var(--q-gold);
  color: var(--q-gold-on, #0e203d);
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.ri-btn-crear:disabled { opacity: 0.6; cursor: not-allowed; }
.ri-btn-crear:hover:not(:disabled) { opacity: 0.88; }

.ri-login-link {
  text-align: center;
  font-size: 0.82rem;
  color: var(--q-muted);
  margin-top: 1rem;
  margin-bottom: 0;
}

.ri-link {
  color: var(--q-gold);
  cursor: pointer;
  font-weight: 600;
}
.ri-link:hover { text-decoration: underline; }

.campo-required {
  color: var(--q-red, #ef4444);
  margin-left: 2px;
}
</style>
